import { createStartOfDay } from "@/lib/calendar"
import i18n from "@/lib/i18n"
import { getUiLocale } from "@/lib/i18n/uiLocales"

export const DAY_MS = 24 * 60 * 60 * 1000
export const DAYS_IN_MONTH = 30
export const DAYS_IN_YEAR = 365

export type DateInput = Date | string | number

interface FormatTimezoneLabelOptions {
  variant?: "default" | "compact" | "without-gmt"
}

const getFormattingLocale = (): string => {
  if (typeof i18n !== "undefined" && i18n.isInitialized) {
    const uiLocale = getUiLocale(i18n.language ?? i18n.resolvedLanguage ?? "en")
    return uiLocale.formattingLocale ?? "en"
  }
  return "en"
}

const prefersHour12 = (): boolean => {
  try {
    return Boolean(
      new Intl.DateTimeFormat(undefined, { hour: "numeric" }).resolvedOptions()
        .hour12,
    )
  } catch {
    return false
  }
}

const padNumber = (value: number) => String(value).padStart(2, "0")

const t = (key: string): string => {
  if (typeof i18n !== "undefined" && i18n.isInitialized) {
    // i18next-allow-dynamic: bounded time.* and weekday_* keys; callers pass static literals
    const result = i18n.t(key as never)
    return result !== key ? (result as string) : key
  }
  return key
}

const toDate = (value: DateInput): Date =>
  value instanceof Date ? value : new Date(value)

export const getDaysLeft = (
  deadline?: DateInput | null,
  referenceTime = Date.now(),
): number => {
  if (deadline == null) return 0
  const remaining = toDate(deadline).getTime() - referenceTime
  return Math.max(0, Math.ceil(remaining / DAY_MS))
}

const isValidDate = (date: Date) => !Number.isNaN(date.getTime())

const formatDate = (
  value: DateInput,
  options: Intl.DateTimeFormatOptions,
  locale = getFormattingLocale(),
) => new Intl.DateTimeFormat(locale, options).format(toDate(value))

const formatDateParts = (
  value: DateInput,
  options: Intl.DateTimeFormatOptions,
  locale = getFormattingLocale(),
) => new Intl.DateTimeFormat(locale, options).formatToParts(toDate(value))

export const formatHourMinute = (
  value: DateInput,
  locale = getFormattingLocale(),
): string => {
  return formatDate(
    value,
    {
      hour: "2-digit",
      minute: "2-digit",
      hour12: prefersHour12(),
    },
    locale,
  )
}

/** Same as formatHourMinute (chat bubbles, etc.). */
export const formatTime = (value: DateInput): string => {
  return formatHourMinute(value)
}

/** `Monday`. */
export const formatLongWeekday = (
  value: DateInput,
  locale = getFormattingLocale(),
): string => {
  return formatDate(value, { weekday: "long" }, locale)
}

export const formatDayMonthTime = (
  value: DateInput,
  month: "long" | "short" = "long",
  locale = getFormattingLocale(),
): string => {
  const parts = formatDateParts(
    value,
    {
      day: "numeric",
      month,
      hour: "2-digit",
      minute: "2-digit",
      hour12: prefersHour12(),
    },
    locale,
  )

  const day = parts.find((part) => part.type === "day")?.value ?? ""
  const monthLabel = parts.find((part) => part.type === "month")?.value ?? ""

  return `${day} ${monthLabel}, ${formatHourMinute(value, locale)}`
}

const getTimezoneOffsetParts = (date: Date) => {
  const offsetMinutes = -date.getTimezoneOffset()
  const sign = offsetMinutes >= 0 ? "+" : "-"
  const absoluteOffset = Math.abs(offsetMinutes)

  return {
    sign,
    hours: Math.floor(absoluteOffset / 60),
    minutes: absoluteOffset % 60,
  }
}

const formatCalendarOffset = (date: Date): string => {
  const { sign, hours, minutes } = getTimezoneOffsetParts(date)
  return `${sign}${padNumber(hours)}:${padNumber(minutes)}`
}

const getRoundedDurationMinutes = (
  startedAt: DateInput,
  endedAt: DateInput,
): number => {
  return Math.max(
    0,
    Math.round(
      (toDate(endedAt).getTime() - toDate(startedAt).getTime()) / 60_000,
    ),
  )
}

/** `27/04` or `27/04/2025` outside the current year. */
export const formatHistoryDate = (value: DateInput): string => {
  const date = toDate(value)
  return formatDate(date, {
    day: "2-digit",
    month: "2-digit",
    ...(date.getFullYear() === new Date().getFullYear()
      ? {}
      : { year: "numeric" }),
  })
}

/** `27 Apr` or `27 Apr 2025` outside the current year. */
export const formatDayShortMonthDate = (value: DateInput): string => {
  const date = toDate(value)

  return formatDate(date, {
    day: "numeric",
    month: "short",
    ...(date.getFullYear() === new Date().getFullYear()
      ? {}
      : { year: "numeric" }),
  })
}

/** `Monday, 27 April 2026`. */
export const formatMeetingDate = (
  value: DateInput,
  options?: { includeYear?: boolean },
): string => {
  return formatDate(value, {
    weekday: "long",
    month: "long",
    day: "numeric",
    ...(options?.includeYear === false ? {} : { year: "numeric" }),
  })
}

/** `14:30`. */
export const formatTranscriptTime = (timestamp: number): string => {
  return formatHourMinute(timestamp)
}

/** `14:30 - 15:15`. */
export const formatMeetingTimeRange = (
  startAt: DateInput,
  durationMinutes: number,
  options?: { hour12?: boolean },
): string => {
  const start = toDate(startAt)
  const end = new Date(start.getTime() + durationMinutes * 60_000)
  const formatTime = (value: Date) =>
    options?.hour12 === undefined
      ? formatHourMinute(value)
      : formatDate(value, {
          hour: "2-digit",
          minute: "2-digit",
          hour12: options.hour12,
        })

  return `${formatTime(start)} - ${formatTime(end)}`
}

/** `14:00 – 15:00 · Asia/Dubai`. */
export const formatMeetingRowTime = (
  startAt: DateInput,
  durationMinutes: number,
): string => {
  const start = toDate(startAt)
  const end = new Date(start.getTime() + durationMinutes * 60_000)
  const timezone = getBrowserIanaTimezone()

  return `${formatHourMinute(start)} – ${formatHourMinute(end)} · ${timezone}`
}

/** `1d 2h`, `2h 30m`, or `45m`. */
export const formatMinutes = (totalMinutes: number): string => {
  const days = Math.floor(totalMinutes / 1440)
  const hours = Math.floor((totalMinutes % 1440) / 60)
  const mins = totalMinutes % 60
  const d = t("time.dayAbbr")
  const h = t("time.hourAbbr")
  const m = t("time.minuteAbbr")
  if (days > 0) {
    return `${days}${d} ${hours}${h}`
  }
  if (hours > 0) {
    return `${hours}${h} ${mins}${m}`
  }
  return `${mins}${m}`
}

/** `1h 30m`, `1h`, `45 min`, or `45m`. */
export const formatDurationMinutes = (
  totalMinutes: number,
  options: { compactMinutes?: boolean } = {},
): string => {
  const normalizedMinutes = Math.max(0, Math.round(totalMinutes))
  const hours = Math.floor(normalizedMinutes / 60)
  const minutes = normalizedMinutes % 60
  const h = t("time.hourAbbr")
  const m = t("time.minuteAbbr")
  const minFull = t("time.minuteFull")

  if (hours > 0 && minutes > 0) {
    return `${hours}${h} ${minutes}${m}`
  }

  if (hours > 0) {
    return `${hours}${h}`
  }

  return options.compactMinutes
    ? `${normalizedMinutes}${m}`
    : `${normalizedMinutes} ${minFull}`
}

/** `1 hr 30 min`, `1 hr`, or `45 min`. */
export const formatDurationHrMin = (totalMinutes: number): string => {
  const normalizedMinutes = Math.max(0, Math.round(totalMinutes))
  const hours = Math.floor(normalizedMinutes / 60)
  const minutes = normalizedMinutes % 60
  const hr = t("time.hourAbbrLong")
  const minFull = t("time.minuteFull")

  if (hours > 0 && minutes > 0) {
    return `${hours} ${hr} ${minutes} ${minFull}`
  }

  if (hours > 0) {
    return `${hours} ${hr}`
  }

  return `${normalizedMinutes} ${minFull}`
}

/** `1h 30m` for the time between two dates. */
export const formatDurationBetween = (
  startedAt: DateInput,
  endedAt: DateInput,
  options?: { compactMinutes?: boolean },
): string => {
  return formatDurationMinutes(
    getRoundedDurationMinutes(startedAt, endedAt),
    options,
  )
}

/** `Mon`. */
export const formatShortWeekday = (value: DateInput): string => {
  return formatDate(value, { weekday: "short" })
}

/** `M` / `ن` — calendar headers (ar short === long, so use narrow). */
export const formatNarrowWeekday = (value: DateInput): string => {
  return formatDate(value, { weekday: "narrow" })
}

/** `Mo` / `ن` — calendar header labels (ar short === long, so use narrow). */
export const formatWeekdayHeaderLabel = (value: DateInput): string => {
  const locale = getFormattingLocale()
  if (locale === "ar") {
    return formatDate(value, { weekday: "narrow" }, locale)
  }
  return formatDate(value, { weekday: "short" }, locale).slice(0, 2)
}

/** Monday-first list of the 7 calendar header weekday labels. */
export const getWeekdayHeaderLabels = (): string[] => {
  const monday = new Date(2026, 0, 5)
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(monday)
    date.setDate(monday.getDate() + index)
    return formatWeekdayHeaderLabel(date)
  })
}

/** `Apr`. */
export const formatShortMonth = (value: DateInput): string => {
  return formatDate(value, { month: "short" })
}

/** `April 2026`. */
export const formatMonthLabel = (value: DateInput): string => {
  return formatDate(value, { month: "long", year: "numeric" })
}

/** `27.04.2026`. */
export const formatDateValue = (value: DateInput): string => {
  const date = toDate(value)
  return `${padNumber(date.getDate())}.${padNumber(date.getMonth() + 1)}.${date.getFullYear()}`
}

/** `09:05`. */
export const formatTimeValue = (hours: number, minutes: number): string => {
  return `${padNumber(hours)}:${padNumber(minutes)}`
}

/** `Europe/Nicosia (GMT+3)` for an IANA timezone at the given instant. */
export const formatIanaTimezoneLabel = (
  timezone: string,
  value: DateInput = new Date(),
  options: FormatTimezoneLabelOptions = {},
): string => {
  const date = toDate(value)
  const gmtLabel = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    timeZoneName: "shortOffset",
  })
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName")?.value

  if (options.variant === "compact") {
    return `(${timezone}${gmtLabel ? `, ${gmtLabel}` : ""})`
  }

  if (options.variant === "without-gmt") {
    return `(${timezone})`
  }

  return `${timezone}${gmtLabel ? ` (${gmtLabel})` : ""}`
}

/** The browser timezone as `Europe/London (GMT+1)` or `(Europe/London, GMT+1)`. */
export const formatTimezoneLabel = (
  value: DateInput = new Date(),
  options: FormatTimezoneLabelOptions = {},
): string => {
  const timezone = getBrowserIanaTimezone()

  return formatIanaTimezoneLabel(timezone, value, options)
}

/** A `YYYY-MM-DD` calendar date for a `Date` (local time). */
export const dateToDateString = (date: Date): string =>
  `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}`

/** Parse a `YYYY-MM-DD` string into a local start-of-day `Date`. */
export const parseDateString = (value: string): Date => {
  const [year, month, day] = value.split("-").map(Number)
  return createStartOfDay(new Date(year, month - 1, day))
}

/** A `YYYY-MM` month string for a `Date`. */
export const monthString = (value: Date): string =>
  `${value.getFullYear()}-${padNumber(value.getMonth() + 1)}`

type BookingHourCycle = "h12" | "h23"

const bookingHourCycle: BookingHourCycle = "h23"

export const bookingHourFormatLabel = (
  {
    h12: "12h",
    h23: "24h",
  } as const
)[bookingHourCycle]

/** `09:30` for an absolute instant rendered in the given timezone. */
export const formatTimeInTimezone = (
  timestamp: number,
  timezone: string,
): string => {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: bookingHourCycle,
  }).format(toDate(timestamp))
}

/** A `YYYY-MM-DD` calendar date for an absolute instant in the given timezone. */
export const formatDateInTimezone = (
  timestamp: number,
  timezone: string,
): string => {
  const wall = getZonedWallClock(timestamp, timezone)
  return `${wall.year}-${padNumber(wall.month)}-${padNumber(wall.day)}`
}

export const getBrowserIanaTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  } catch {
    return "UTC"
  }
}

type ZonedWallClock = {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

const getDateTimePartsInTimezone = (
  value: DateInput,
  timezone: string,
): ZonedWallClock => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(toDate(value))
  const values = Object.fromEntries(
    parts
      .filter(({ type }) => type !== "literal")
      .map(({ type, value: partValue }) => [type, partValue]),
  )

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
    hour: Number(values.hour),
    minute: Number(values.minute),
    second: Number(values.second),
  }
}

export const getZonedWallClock = (
  value: DateInput,
  timezone: string,
): ZonedWallClock => getDateTimePartsInTimezone(value, timezone)

const getTimeZoneOffsetMs = (date: Date, timezone: string): number => {
  const wall = getDateTimePartsInTimezone(date, timezone)
  const asUtc = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
  )
  return asUtc - date.getTime()
}

const getZonedWallClockKey = (parts: {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}): number =>
  Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  )

export const zonedWallClockToDate = (
  parts: {
    year: number
    month: number
    day: number
    hour: number
    minute: number
    second?: number
  },
  timezone: string,
): Date => {
  const utcGuess = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second ?? 0,
  )

  const probeOffsets = [
    -172_800_000, -86_400_000, -43_200_000, 0, 43_200_000, 86_400_000,
    172_800_000,
  ].map((probeOffset) =>
    getTimeZoneOffsetMs(new Date(utcGuess + probeOffset), timezone),
  )
  const offsets = [...new Set(probeOffsets)]
  const requestedWallClockKey = utcGuess
  const candidates = offsets.map((offset) => new Date(utcGuess - offset))
  const matchingCandidates = candidates.filter(
    (candidate) =>
      getZonedWallClockKey(getDateTimePartsInTimezone(candidate, timezone)) ===
      requestedWallClockKey,
  )

  if (matchingCandidates.length > 0) {
    return new Date(
      Math.max(...matchingCandidates.map((candidate) => candidate.getTime())),
    )
  }

  const forwardCandidates = candidates
    .map((candidate) => ({
      candidate,
      wallClockKey: getZonedWallClockKey(
        getDateTimePartsInTimezone(candidate, timezone),
      ),
    }))
    .filter(({ wallClockKey }) => wallClockKey >= requestedWallClockKey)
    .sort((first, second) => first.wallClockKey - second.wallClockKey)

  return forwardCandidates[0]?.candidate ?? candidates[0]
}

export const createZonedDateTime = (
  date: Date,
  hours: number,
  minutes: number,
  timezone: string,
): Date =>
  zonedWallClockToDate(
    {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: hours,
      minute: minutes,
    },
    timezone,
  )

export const zonedInstantToCalendarDate = (
  value: DateInput,
  timezone: string,
): Date => {
  const wall = getZonedWallClock(value, timezone)
  return createStartOfDay(new Date(wall.year, wall.month - 1, wall.day))
}

const fullWeekdayKeys = [
  "weekday_sunday_full",
  "weekday_monday_full",
  "weekday_tuesday_full",
  "weekday_wednesday_full",
  "weekday_thursday_full",
  "weekday_friday_full",
  "weekday_saturday_full",
] as const

/** A date-only label such as `Tuesday 10` rendered for a `YYYY-MM-DD` value. */
export const formatDayMonthLabel = (dateString: string): string => {
  const date = parseDateString(dateString)
  const day = formatDate(date, { day: "numeric" })
  return `${t(fullWeekdayKeys[date.getDay()])} ${day}`
}

/** `2026-04-27T14:30:00+01:00`. */
export const formatCalendarTimestamp = (timestamp: number): string => {
  const date = new Date(timestamp)

  return `${date.getFullYear()}-${padNumber(date.getMonth() + 1)}-${padNumber(date.getDate())}T${padNumber(date.getHours())}:${padNumber(date.getMinutes())}:${padNumber(date.getSeconds())}${formatCalendarOffset(date)}`
}

export const formatCalendarTimestampInTimezone = (
  timestamp: number,
  timezone: string,
): string => {
  const date = new Date(timestamp)
  const wall = getZonedWallClock(date, timezone)
  const offsetMinutes = Math.round(getTimeZoneOffsetMs(date, timezone) / 60_000)
  const sign = offsetMinutes >= 0 ? "+" : "-"
  const absoluteOffset = Math.abs(offsetMinutes)

  return `${wall.year}-${padNumber(wall.month)}-${padNumber(wall.day)}T${padNumber(wall.hour)}:${padNumber(wall.minute)}:${padNumber(wall.second)}${sign}${padNumber(Math.floor(absoluteOffset / 60))}:${padNumber(absoluteOffset % 60)}`
}

/** `03:25` or `01:03:25`. */
export const formatElapsedDuration = (seconds: number): string => {
  const normalizedSeconds = Math.max(0, Math.floor(seconds))
  const hours = Math.floor(normalizedSeconds / 3600)
  const minutes = Math.floor((normalizedSeconds % 3600) / 60)
  const remainingSeconds = normalizedSeconds % 60

  if (hours > 0) {
    return `${padNumber(hours)}:${padNumber(minutes)}:${padNumber(remainingSeconds)}`
  }

  return `${padNumber(minutes)}:${padNumber(remainingSeconds)}`
}

/** `45 sec` or `3 min`. */
export const formatDurationSec = (seconds: number): string => {
  const secs = Math.max(0, Math.floor(seconds))
  const sec = t("time.secondAbbr")
  const minFull = t("time.minuteFull")
  if (secs < 60) {
    return `${secs} ${sec}`
  }
  return `${Math.floor(secs / 60)} ${minFull}`
}

/** `1 hour`, `1.5 hour`, `2 hour`, or `45 min`. */
export const formatMeetingDurationLabel = (durationMinutes: number): string => {
  const hour = t("time.hourFull")
  const minFull = t("time.minuteFull")
  return (
    {
      60: `1 ${hour}`,
      90: `1.5 ${hour}`,
      120: `2 ${hour}`,
    }[durationMinutes] ?? `${durationMinutes} ${minFull}`
  )
}

const formatShortMonthDayBase = (
  value?: DateInput | null,
  withYear = false,
  locale = getFormattingLocale(),
): string | undefined => {
  if (value == null) return undefined

  try {
    const date = toDate(value)

    if (!isValidDate(date)) {
      return undefined
    }

    const options: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
      ...(withYear ? { year: "numeric" } : {}),
    }

    return formatDate(date, options, locale)
  } catch {
    return undefined
  }
}

/** `Apr 27`. */
export const formatShortMonthDay = (
  value?: DateInput | null,
  locale?: string,
): string | undefined => formatShortMonthDayBase(value, false, locale)

/** `Apr 27, 2026`. */
export const formatShortMonthDayYear = (
  value?: DateInput | null,
  locale?: string,
): string | undefined => formatShortMonthDayBase(value, true, locale)

export const formatShortMonthDayForLocale = (
  value: DateInput,
  locale: string,
): string => formatDate(value, { month: "short", day: "numeric" }, locale)

export const formatShortMonthDayYearCurrentLocale = (
  value: DateInput,
): string =>
  formatDate(value, {
    month: "short",
    day: "numeric",
    year: "numeric",
  })

export const formatDayOfMonth = (value: DateInput): number =>
  toDate(value).getDate()
